<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import type { SiteBlock } from '~/types/types'

const props = defineProps<{ block: SiteBlock }>()

// Upload goes through the editor adapter provided by the settings panel, so
// this form has no direct dependency on the app's upload repository.
const { upload } = useSiteEditorAdapters()

const fileInput = ref<HTMLInputElement | null>(null)
const uploading = ref(false)

const pickHtmlFile = () => fileInput.value?.click()

const onUploadHtml = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return

  uploading.value = true
  try {
    const result = await upload(file, 'html')
    props.block.data.file_url = result.url
  } finally {
    uploading.value = false
    target.value = ''
  }
}
</script>

<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <UFormField
    label="HTML file"
    :error="!block.data.file_url ? 'This block needs an uploaded file before the page can be saved.' : undefined"
  >
    <div class="flex items-center gap-2">
      <UButton
        size="sm"
        variant="outline"
        color="neutral"
        icon="i-lucide-upload"
        :loading="uploading"
        @click="pickHtmlFile"
      >
        Upload
      </UButton>
      <span
        v-if="block.data.file_url"
        class="truncate text-xs ui-text-subtle"
      >{{ block.data.file_url }}</span>
    </div>
  </UFormField>
  <UFormField label="Height (px)">
    <UInput
      v-model.number="block.data.height"
      type="number"
      min="200"
      max="4000"
      class="w-full"
    />
  </UFormField>
  <p class="text-xs ui-text-subtle">
    Runs sandboxed — the uploaded page can execute its own scripts but can't read cookies, local storage, or anything else on your site. Only upload files you trust.
  </p>

  <input
    ref="fileInput"
    type="file"
    accept=".html,.htm,text/html"
    class="hidden"
    @change="onUploadHtml"
  >
</template>
